import { ClassNames } from "@emotion/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { callAllHandlers, useIsComposingHandler } from "@smartsheet/lodestar-utils";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import React, { useState, forwardRef, ReactNode, useRef, useEffect, useCallback } from "react";
import { Omit } from "utility-types";
import { noop } from "../../constants/noop";
import { useTheme } from "../../theme/utils/useTheme";
import { getInputFieldHeightValue } from "../../util/inputFieldBaseProps";
import { InputFieldBaseProps } from "../../util/inputFieldBaseProps";
import { useFocusMode } from "../focusMode";
import { formInputTheme, getStylingState } from "../textinput/styled/formInputTheme";
import { GROW_WRAPPER_CLASS } from "./constants";
import { GrowWrapperStyled } from "./styled/GrowWrapperStyled";
import { StyledContentAfterContainer } from "./styled/StyledContentAfterContainer";
import { StyledResizer } from "./styled/StyledResizer";
import { StyledTextAreaRoot, LINE_HEIGHT_PX } from "./styled/StyledTextAreaRoot";
import { TextAreaStyled } from "./styled/TextAreaStyled";

export interface TextAreaProps
    extends InputFieldBaseProps,
        Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value" | "disabled"> {
    /** Handler to be called when the input changes. */
    onChange: (value: string, event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    /** The value of the textarea. */
    value: string;
    /**
     * A `clientId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-client-id` in the rendered code,
     * serving as a hook for automated tests */
    clientId?: string;

    /**
     * Piece of UI to show after the typeable area, normally a borderless medium-sized button with a single icon (size 24) inside.
     */
    contentAfter?: ReactNode;
    /**
     * Don't allow users to manually resize the textarea. This can be advantageous if you want the
     * textarea to always grow along with its content.
     */
    disableManualResize?: boolean;
    /** Flag to determine if the input is disabled */
    isDisabled?: boolean;
    /** The max height of the textarea, e.g. "400px". If a number is passed, this will be treated like the HTML `rows` attribute. */
    maxHeight?: string | number;
    /**
     * The min height of the textarea, e.g. "48px". Text areas are always at least one line tall,
     * so you can use "0" to get that minimal one line height. If a number is passed, this will be treated like the HTML `rows` attribute. */
    minHeight?: string | number;
    /**
     * The style of the textarea. This is the style of the textarea itself, not the wrapper.
     */
    textAreaStyles?: React.CSSProperties;
    /** If "error" then it gets a red border. */
    validationState?: "error";
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>((props, parentRef) => {
    const {
        disableManualResize = false,
        value,
        onChange,
        minHeight,
        maxHeight,
        contentAfter,
        clientId,
        isDisabled = false,
        style: propStyle,
        validationState,
        onBlur,
        onFocus,
        onKeyDown,
        onKeyUp,
        onMouseEnter,
        onMouseLeave,
        textAreaStyles,
        inputFieldHeight: inputFieldHeightProp,
        ...htmlAttributes
    } = props;
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { focusMode } = useFocusMode();
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [wrapperValue, setWrapperValue] = useState<string>(value);
    const cursorRef = useRef<string | undefined>();

    const textAreaRef = useEnsuredForwardedRef(parentRef);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const inputFieldHeight = getInputFieldHeightValue(themeMode, inputFieldHeightProp);
    const borderWidth: number =
        formInputTheme.borderWidth[getStylingState({ validationState, isFocused, focusMode })](themeMode);
    const topPadding =
        formInputTheme.padding.top[inputFieldHeight][getStylingState({ validationState, isFocused, focusMode })](
            themeMode
        );
    const bottomPadding =
        formInputTheme.padding.bottom[inputFieldHeight][getStylingState({ validationState, isFocused, focusMode })](
            themeMode
        );
    const bordersPlusPadding = borderWidth * 2 + topPadding + bottomPadding;
    const absoluteMinHeight: number = bordersPlusPadding + Math.floor(LINE_HEIGHT_PX);

    const calculatedMinHeight: string =
        typeof minHeight === "number"
            ? `${minHeight * formInputTheme.lineHeight(themeMode) + bordersPlusPadding}px`
            : minHeight || `${absoluteMinHeight}px`;
    const calculatedMaxHeight: string | undefined =
        typeof maxHeight === "number"
            ? `${maxHeight * formInputTheme.lineHeight(themeMode) + bordersPlusPadding}px`
            : maxHeight;

    function handleOnChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        setWrapperValue(e.target.value);
        if (onChange) {
            onChange(e.target.value, e);
        }
    }

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (textAreaRef.current && wrapperRef.current) {
                // Get the top position of the wrapper element relative to the viewport
                const { top } = wrapperRef.current.getBoundingClientRect();
                // Adjust for any vertical scrolling
                const offsetTop = top + window.scrollY;
                // Calculate the new height based on the current mouse Y-coordinate
                const height = e.pageY - offsetTop;
                // Apply the calculated height to the wrapper element
                wrapperRef.current.style.height = `${height}px`;
            }
        },
        [wrapperRef, textAreaRef]
    );

    const handleKeyDown = useIsComposingHandler(onKeyDown || noop);
    const handleKeyUp = useIsComposingHandler(onKeyUp || noop);

    const handleCleanUp = useCallback(() => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseMove);
        // reset the cursor style when user drag is done. This does not appear to affect styles set higher in the dom tree.
        if (document?.body) {
            document.body.style.cursor = cursorRef.current || "auto";
        }
    }, [handleMouseMove]);

    const handleMouseDown = (e: React.MouseEvent) => {
        // necessary to prevent highlighting the text when resizing
        e.preventDefault();
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleCleanUp);
        if (document?.body) {
            // since we've manufactured a resizer, we need to know when we're dragging (since a user can drag off of the target, losing css hover state)
            document.body.style.cursor = "ns-resize";
        }
    };

    useEffect(() => {
        // we need to grab this value in case it isn't "auto", so we can reset it later
        if (document?.body) {
            const initialCursor = document.body.style.cursor;
            cursorRef.current = initialCursor;
        }
        return () => {
            handleCleanUp();
        };
    }, [handleCleanUp]);

    return (
        <ClassNames>
            {({ cx }) => (
                <StyledTextAreaRoot
                    inputFieldHeight={inputFieldHeight}
                    themeMode={themeMode}
                    isDisabled={isDisabled}
                    isFocused={isFocused}
                    ref={wrapperRef}
                    isHovered={isHovered}
                    focusMode={focusMode}
                    onClick={() => {
                        !isDisabled && textAreaRef.current?.focus();
                    }}
                    style={{
                        maxHeight: calculatedMaxHeight,
                        minHeight: `max(${absoluteMinHeight}px, ${calculatedMinHeight})`,
                        ...propStyle,
                    }}
                    validationState={validationState}
                    hasContentAfter={!!contentAfter}
                    className={htmlAttributes.className}
                >
                    {!disableManualResize && !isDisabled && (
                        <StyledResizer
                            themeMode={themeMode}
                            isFocused={isFocused}
                            focusMode={focusMode}
                            validationState={validationState}
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleCleanUp}
                        />
                    )}

                    <GrowWrapperStyled
                        themeMode={themeMode}
                        isFocused={isFocused}
                        validationState={validationState}
                        focusMode={focusMode}
                        inputFieldHeight={inputFieldHeight}
                    >
                        <TextAreaStyled
                            isDisabled={isDisabled}
                            isFocused={isFocused}
                            isHovered={isHovered}
                            focusMode={focusMode}
                            // this is a default, which will be overridden by content, minHeight, maxHeight
                            rows={4}
                            maxHeight={
                                calculatedMaxHeight ? `calc(${calculatedMaxHeight} - ${borderWidth * 2}px)` : undefined
                            }
                            minHeight={`calc(${calculatedMinHeight} - ${borderWidth * 2}px)`}
                            value={value}
                            onChange={handleOnChange}
                            onKeyDown={(onKeyDown && handleKeyDown) || undefined}
                            onKeyUp={(onKeyUp && handleKeyUp) || undefined}
                            onFocus={callAllHandlers(() => setIsFocused(true), onFocus)}
                            onBlur={callAllHandlers(() => setIsFocused(false), onBlur)}
                            onMouseEnter={callAllHandlers(() => setIsHovered(true), onMouseEnter)}
                            onMouseLeave={callAllHandlers(() => setIsHovered(false), onMouseLeave)}
                            ref={textAreaRef}
                            validationState={validationState}
                            data-client-id={clientId}
                            themeMode={themeMode}
                            aria-disabled={isDisabled}
                            {...htmlAttributes}
                            style={textAreaStyles}
                            readOnly={isDisabled ? true : htmlAttributes.readOnly ? htmlAttributes.readOnly : false}
                            inputFieldHeight={inputFieldHeight}
                        />
                        {/* &nbps; necessary to add height when a new row is empty */}
                        <div className={cx(GROW_WRAPPER_CLASS, htmlAttributes.className)} style={textAreaStyles}>
                            {wrapperValue}&nbsp;
                        </div>
                    </GrowWrapperStyled>
                    {contentAfter && (
                        <StyledContentAfterContainer
                            themeMode={themeMode}
                            isFocused={isFocused}
                            focusMode={focusMode}
                            validationState={validationState}
                        >
                            {contentAfter}
                        </StyledContentAfterContainer>
                    )}
                </StyledTextAreaRoot>
            )}
        </ClassNames>
    );
});

TextArea.displayName = "TextArea";
